import { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import Sticky from 'react-sticky-el'
import { Container, Row, Col } from 'reactstrap'
import { recalculateTaxIfReady } from '../../redux/storeConfig/slices/slice.checkoutTax'
import { cartItemsRequireCheckForApproval, returnCartItemsRequireApprovalError } from '../Cart/cartFunctions'
import LoadingSpinner from '../general/LoadingSpinner'
import CheckoutAddressDisplay from './CheckoutAddressDisplay'
import CheckoutCardPanel from './CheckoutCardPanel'
import CheckoutCreditBalances from './CheckoutCreditBalances'
import CheckoutErrorsPanel from './CheckoutErrorsPanel'
import { setCustomerAddress, setErrors } from './checkoutFunctions'
import CheckoutInformation from './CheckoutInformation'
import OrderSummary from './CheckoutOrderSummary'
import PromotionsPanel from './CheckoutPromotionsPanel'
import { useCheckoutInitialization } from './hooks/useCheckoutInitialization'
import { useShippingCalculation } from './hooks/useShippingCalculation'
import ShippingMethodSelector from './ShippingMethodSelector'
import './style.css'

export default function CheckoutPage() {
  const dispatch = useDispatch()
  const {
    errors,
    checkout,
    portal,
    currentLocation,
    cartItems,
    cartLoading,
    currentUser,
    checkoutRates,
    integrations,
    checkoutSessionShortCode,
    customerAddresses,
    managedCustomerAddresses
  } = useSelector(state => ({
    errors: state.errors,
    checkout: state.checkout,
    portal: state.portal,
    currentLocation: state.storeViews.current,
    cartItems: state.cartItems,
    cartLoading: state.checkout.cart_loading,
    currentUser: state.currentUser,
    checkoutRates: state.checkoutRates,
    integrations: state.integrations,
    checkoutSessionShortCode: state.checkoutSessionToken.shortCode,
    customerAddresses: state.customer_addresses,
    managedCustomerAddresses: state.managed_customer_addresses
  }))

  const isCartLoaded = useMemo(() => cartItems?.length > 0, [cartItems])
  const [isZoomedIn, setIsZoomedIn] = useState(() => Math.round(window.devicePixelRatio * 100) > 100)

  const detectZoom = useCallback(() => {
    const zoomLevel = Math.round(window.devicePixelRatio * 100)
    setIsZoomedIn(zoomLevel > 100)
  }, [])

  useEffect(() => {
    window.addEventListener('resize', detectZoom)
    return () => window.removeEventListener('resize', detectZoom)
  }, [detectZoom])

  // Initialize checkout
  const { isInitializing } = useCheckoutInitialization(currentLocation, currentUser)

  // Resolve checkout session token address after refresh (addresses may load after token)
  useEffect(() => {
    const pendingAddressId = checkout?.sessionTokenAddressId
    if (!pendingAddressId) return

    const allAddresses = [...(customerAddresses ?? []), ...Object.values(managedCustomerAddresses ?? {})]

    if (allAddresses.length === 0) return

    const address = allAddresses.find(a => a.id === pendingAddressId)
    if (!address) return

    dispatch(setCustomerAddress(address)).then(() => {
      dispatch(recalculateTaxIfReady())
    })
  }, [checkout?.sessionTokenAddressId, customerAddresses, managedCustomerAddresses, dispatch])

  useEffect(() => {
    if (currentUser?.id) {
      // Only update contactEmail error if it's currently set to true
      if (checkout?.errors?.contactEmail) dispatch(setErrors({ contactEmail: false }))
    } else if (checkout?.contactEmail) {
      // Guest checkout with contact email entered
      if (checkout?.errors?.contactEmail) dispatch(setErrors({ contactEmail: false }))
    }
  }, [checkout?.errors?.contactEmail, checkout?.contactEmail, currentUser?.id, dispatch])

  useEffect(() => {
    if (cartItems?.length > 0 && checkout?.errors?.shipping) {
      const allItemsDigitalDelivery = cartItems?.every(cartItem => cartItem.shippable_type === 'Digital Delivery')

      // If all items are digital delivery, clear shipping error since no shipping is required
      if (allItemsDigitalDelivery) dispatch(setErrors({ shipping: false }))
      // For regular items, clear shipping error once cart is loaded (shipping will be calculated separately)
      else dispatch(setErrors({ shipping: false }))
    }
  }, [cartItems, checkout?.errors?.shipping, dispatch])

  useEffect(() => {
    const legacyShippingErrorExists = errors?.shipping_errors && errors?.shipping_errors?.message

    // Only set shipping error to true if there's a legacy error and no current checkout shipping error
    if (legacyShippingErrorExists && !checkout?.errors?.shipping) dispatch(setErrors({ shipping: true }))
  }, [checkout?.errors?.shipping, dispatch, errors?.shipping_errors])

  // Get calculation functions from hooks (without automatic triggering)
  const { calculateShipping } = useShippingCalculation({
    isInitializing,
    isCartLoaded,
    cartItems,
    checkout,
    portal,
    integrations,
    checkoutRates
  })

  // Create stable serialized key for multiAddressSelection to prevent unnecessary recalculations
  // This prevents shipping from re-fetching when SET_CART_ITEMS creates a new array reference
  // but the actual selection content hasn't changed
  const multiAddressSelectionKey = useMemo(
    () => JSON.stringify(checkout?.multiAddressSelection),
    [checkout?.multiAddressSelection]
  )

  // Use ref to store the latest callback function with stable reference
  const calculateShippingRef = useRef(calculateShipping)

  // Update ref after every render (doesn't trigger effects)
  useEffect(() => {
    calculateShippingRef.current = calculateShipping
  })

  // Trigger shipping calculations when address, multi-address selections, or shipping payment method changes
  useEffect(() => {
    // Don't calculate during initialization or if cart isn't loaded
    if (isInitializing || !isCartLoaded) return

    // Call the latest version of the callback via ref (stable reference)
    calculateShippingRef.current()
  }, [
    isInitializing,
    isCartLoaded,
    checkout?.selectedCustomerAddress?.id,
    multiAddressSelectionKey,
    integrations?.status,
    checkoutRates?.shippingPaymentMethod
  ])

  // Check if cart items contain both requires approval and does not require approval
  useEffect(() => {
    // Don't check during initialization or if cart isn't loaded
    if (isInitializing || !isCartLoaded) return

    if (cartItems && cartItems.length > 0 && cartItemsRequireCheckForApproval(null, cartItems, currentLocation))
      dispatch(returnCartItemsRequireApprovalError(cartItems, null))
  }, [cartItems, currentLocation, dispatch, isCartLoaded, isInitializing])

  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 992px)' })

  const fixedHeaderElement = document.querySelector('.iron-fixed-header')
  const menuHeight = fixedHeaderElement !== null ? fixedHeaderElement.offsetHeight : '100px'

  const screenHeight = window.screen.availHeight
  const responseHeight = screenHeight !== null && screenHeight <= 770 ? true : false
  const showCheckoutInformation = currentLocation.show_checkout_information && currentLocation.checkout_form?.length > 0
  return (
    <div>
      <Container className="mt-30 mb-30">
        <Row>
          <Col xs={12} md={7} lg={8}>
            {showCheckoutInformation && <CheckoutInformation />}
            <CheckoutAddressDisplay />
            <CheckoutErrorsPanel />
            {cartLoading ? <LoadingSpinner /> : <CheckoutCreditBalances />}
            <CheckoutCardPanel extraFields={currentUser.run_automation_and_extra_fields} />
            {/* ShippingMethodSelector handles all shipping UI: rates, loading, errors, ShipperHQ, third party billing */}
            <ShippingMethodSelector />
          </Col>
          <Col xs={12} md={5} lg={4} className="right-panel-promotions-summary">
            <Sticky
              className="sticky-right-panel"
              topOffset={0}
              stickyStyle={{
                top: menuHeight >= 90 ? '160px' : '100px',
                zIndex: 2
              }}
              disabled={isTabletOrMobile || responseHeight || isZoomedIn}
            >
              <PromotionsPanel />
              <OrderSummary isCheckout={true} />
            </Sticky>
          </Col>
        </Row>
      </Container>

      {/* Session Reference Code - positioned at bottom center */}
      {checkoutSessionShortCode && (
        <div
          style={{
            textAlign: 'center',
            padding: '12px 0',
            marginTop: '20px'
          }}
        >
          <small style={{ color: '#999', fontSize: '11px' }}>ref: {checkoutSessionShortCode}</small>
        </div>
      )}
    </div>
  )
}
